"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import type { LaunchSibling } from "@/lib/virtualspace/launch-siblings";

/**
 * Launch active-model context (2026-05-23, Phase 1).
 *
 * Owned by `<LaunchActiveModelProvider>`. Lets the menu / elevator
 * strip (which know the SIBLING that was clicked) communicate to
 * the iframe-rendering MatterportBlock (which needs the new
 * provider_model_id to swap the SDK).
 *
 * Why a context (vs. lifting state to LaunchPublicShell + prop
 * threading):
 *   - The renderer chain (LaunchPublicShell → renderer →
 *     MatterportBlock → MatterportSdkLaunchBlock) is 4 levels
 *     deep and the existing block components don't accept a
 *     "modelId override" prop. Context avoids touching every
 *     intermediate component's prop interface.
 *   - Siblings are a Launch-only concern; multi-block Orbit /
 *     Landing pages don't render via this path, so the context
 *     stays scoped to Launch and doesn't leak.
 *
 * URL hash sync:
 *   - `#m=<deliverableId>` selects a sibling, so a refresh of
 *     `/launch/<token>#m=<otherSibling>` lands you back on that
 *     sibling instead of the token's own deliverable.
 *   - On selection, push `#m=<deliverableId>` via
 *     history.replaceState (no back-stack pollution — each
 *     sibling switch isn't a separate "page" in the browser
 *     sense).
 *   - The menu preserves the current share token and selects the
 *     active sibling with `#m`, retaining the same presentation
 *     and access rules when the link is copied.
 *
 * Why the hash is read in a layout effect and NOT in the
 * `useState` initializer (fixed 2026-08-13):
 *   A URL fragment is never sent to the server, so a render-phase
 *   read of `window.location.hash` can only ever disagree with the
 *   server's markup. Seeding state from it made every
 *   `#m=<sibling>` load — including the refresh-after-selection
 *   path this provider exists to support — fail hydration and
 *   regenerate the whole Launch tree on the client. Reconciling in
 *   a layout effect instead renders the server's sibling on both
 *   sides of hydration and applies the fragment before the browser
 *   paints the hydrated frame.
 *
 *   `initialDeliverableId` is the honest server-side value here:
 *   unlike a search param, `#m=` has no server-visible counterpart
 *   on `/launch/<token>` (the route reads `params` only), and the
 *   canonical way to deep-link one sibling is already that
 *   sibling's OWN share token. Mirroring `#m=` into a query param
 *   would change the URL contract of public links customers have
 *   already shared, so we don't.
 */

export type LaunchActiveModelContextValue = {
  activeDeliverableId: string | null;
  activeProviderModelId: string | null;
  /** Active sibling's canonical share token (used to compose the
   *  copy/share URL via the host). Null when the active sibling
   *  has no share token yet. */
  activeShareToken: string | null;
  /** Switch to a different sibling. No-op when id doesn't match
   *  a known sibling. */
  setActiveDeliverableId: (deliverableId: string) => void;
  /** Sibling list (read-only — provider owns it). */
  siblings: ReadonlyArray<LaunchSibling>;
};

const LaunchActiveModelContext =
  createContext<LaunchActiveModelContextValue | null>(null);

export function useLaunchActiveModel(): LaunchActiveModelContextValue | null {
  return useContext(LaunchActiveModelContext);
}

/** Matches `#m=<value>`, either as the whole fragment or as one
 *  `&`-separated part of it. */
const HASH_MODEL_PATTERN = /[#&]m=([^&]+)/;

/** `useLayoutEffect` warns when it runs during SSR, so fall back to
 *  `useEffect` on the server. Matches `SurveyOverviewVisibility` and
 *  `useSurveyDeckPosition`. */
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Resolve `#m=<deliverableId>` against the known sibling set.
 * Returns null when the fragment carries no `m=` part, when the
 * value doesn't decode, or when it names a sibling this Launch
 * doesn't have.
 *
 * Exported for tests. Pure — takes the fragment as an argument
 * rather than reading `window`, so it is safe to call from either
 * environment.
 */
export function siblingIdFromHash(
  hash: string,
  siblings: ReadonlyArray<LaunchSibling>,
): string | null {
  const match = hash.match(HASH_MODEL_PATTERN);
  if (!match) return null;
  let candidate: string;
  try {
    candidate = decodeURIComponent(match[1]!);
  } catch {
    // A truncated or hand-mangled escape (`#m=%`) used to throw a
    // URIError out of the render phase, which took the menu and
    // elevator strip down via the root error boundary. This is a
    // public, unauthenticated URL that anyone can craft and send, so
    // an unparseable fragment has to degrade to "no selection".
    return null;
  }
  return siblings.some((s) => s.deliverableId === candidate)
    ? candidate
    : null;
}

/**
 * Provider. Pass the loaded siblings + initial active id (typically
 * `siblings.find(s => s.isCurrent)?.deliverableId`).
 *
 * Children re-render when the active model changes (via context
 * consumption — only descendants that call `useLaunchActiveModel`
 * subscribe).
 */
export function LaunchActiveModelProvider({
  siblings,
  initialDeliverableId,
  children,
}: {
  siblings: ReadonlyArray<LaunchSibling>;
  initialDeliverableId: string | null;
  children: React.ReactNode;
}) {
  // Start from the sibling the SERVER chose (derived from the token
  // in the URL path), so the first client render reproduces the
  // server's markup exactly.
  const [activeId, setActiveId] = useState<string | null>(
    initialDeliverableId,
  );

  // Apply `#m=` before the browser paints the hydrated frame, then
  // keep tracking forward/back navigation that rewrites the hash.
  //
  // Re-running this is safe: `setActiveDeliverableId` always writes
  // the selection back to the hash, so re-deriving from the URL bar
  // reproduces the visitor's current choice rather than discarding
  // it.
  useIsomorphicLayoutEffect(() => {
    const syncFromHash = () => {
      const fromHash = siblingIdFromHash(window.location.hash, siblings);
      if (fromHash) {
        setActiveId(fromHash);
        return;
      }
      // No `m=` part at all (a plain load, or the hash cleared by a
      // back navigation) → the token's own sibling. A present-but-
      // unrecognized value leaves the current selection alone.
      if (!HASH_MODEL_PATTERN.test(window.location.hash)) {
        setActiveId(initialDeliverableId);
      }
    };
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [siblings, initialDeliverableId]);

  const setActiveDeliverableId = useCallback(
    (deliverableId: string) => {
      if (!siblings.some((s) => s.deliverableId === deliverableId)) return;
      setActiveId(deliverableId);
      // Update hash WITHOUT a back-stack entry (subsequent switches
      // overwrite the previous hash; back button leaves the Launch
      // entirely, which is what customers expect).
      if (typeof window !== "undefined") {
        const target = `${window.location.pathname}${window.location.search}#m=${encodeURIComponent(deliverableId)}`;
        window.history.replaceState(window.history.state, "", target);
      }
    },
    [siblings],
  );

  const value = useMemo<LaunchActiveModelContextValue>(() => {
    const activeSibling = activeId
      ? siblings.find((s) => s.deliverableId === activeId) ?? null
      : null;
    return {
      activeDeliverableId: activeSibling?.deliverableId ?? initialDeliverableId,
      activeProviderModelId: activeSibling?.providerModelId ?? null,
      activeShareToken: activeSibling?.shareToken ?? null,
      setActiveDeliverableId,
      siblings,
    };
  }, [activeId, siblings, initialDeliverableId, setActiveDeliverableId]);

  return (
    <LaunchActiveModelContext.Provider value={value}>
      {children}
    </LaunchActiveModelContext.Provider>
  );
}
